import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import Loading from '../../../components/Loading';
import { getDate } from '../../../utils/common';
import { useGuardarLugarOcurrenciaMutation } from '../../../services/roamApi';
import { useGetProvincesQuery, useLazyGetCitiesQuery } from '../../../services/companyApi';
import { formatNombreConCuit } from '../utils/formatNombreConCuit';

interface SiniestroInformationTabProps {
    siniestro?: any;
    siniestroId?: number;
    canEditOccurrenceLocation: boolean;
    isLoading: boolean;
    onRefreshSiniestro: () => Promise<any>;
}

interface FieldProps {
    label: string;
    value?: string | null;
    isLoading?: boolean;
}

interface ProvinceOption {
    id: number;
    nombre: string;
}

interface CityOption {
    id: number;
    nombre: string;
    codigoPostal?: number;
}

interface OccurrenceFormErrors {
    calle?: string;
    altura?: string;
    idProvincia?: string;
    idLocalidad?: string;
}

interface OccurrenceDraft {
    calle: string;
    altura: string;
    piso: string;
    depto: string;
    codigoPostal: string;
    selectedProvince: ProvinceOption | null;
    selectedCity: CityOption | null;
}

const hasValue = (value: unknown) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

const hasOccurrenceLocationData = (siniestro?: any) => {
    const lugarOcurrencia = siniestro?.lugarOcurrencia || {};

    return ['calle', 'altura', 'piso', 'depto', 'codigoPostal'].some((field) =>
        hasValue(lugarOcurrencia?.[field])
    );
};

const Field = ({ label, value, isLoading = false }: FieldProps) => (
    <Box>
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                mb: 0.75,
                color: '#98A2B3',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.04em'
            }}
        >
            {label}
        </Typography>
        {isLoading ? (
            <Loading size="extrasmall" position="flex-start" />
        ) : (
            <Typography
                variant="body2"
                sx={{ color: '#344054', fontWeight: 600, whiteSpace: 'pre-line' }}
            >
                {value || '-'}
            </Typography>
        )}
    </Box>
);

const getDisplayValue = (...values: any[]): string => {
    for (const value of values) {
        if (Array.isArray(value)) {
            const joinedValues: string = value
                .map((item) => getDisplayValue(item))
                .filter(Boolean)
                .join(', ');

            if (joinedValues) return joinedValues;
        }
        if (typeof value === 'string' && value.trim()) return value;
        if (typeof value === 'number') return String(value);
        if (value?.nombre) return value.nombre;
        if (value?.descripcion) return value.descripcion;
        if (value?.label) return value.label;
    }

    return '';
};

interface SectionProps {
    title: ReactNode;
    children: ReactNode;
    titleAdornment?: ReactNode;
    rightContent?: ReactNode;
}

export const Section = ({ title, children, titleAdornment, rightContent }: SectionProps) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #EAECF0', borderRadius: 1.5, overflow: 'hidden' }}
    >
        <Box
            sx={{ px: 2, py: 1.25, borderBottom: '1px solid #EAECF0', backgroundColor: '#FCFCFD' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#344054',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: 12
                        }}
                    >
                        {title}
                    </Typography>
                    {titleAdornment}
                </Box>
                {rightContent}
            </Box>
        </Box>
        <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
);

const formatOccurrenceAddress = (values: {
    calle: string;
    altura: string;
    piso: string;
    depto: string;
}) => {
    const streetLine = [values.calle, values.altura].filter(Boolean).join(' ').trim();
    const floorLine = [
        values.piso && `Piso ${values.piso}`,
        values.depto && `Depto ${values.depto}`
    ]
        .filter(Boolean)
        .join(' ')
        .trim();

    return [streetLine, floorLine].filter(Boolean).join('\n');
};

const getOccurrenceFormInitialValues = (siniestro?: any, savedDraft?: OccurrenceDraft | null) => {
    const shouldUseLugarTrabajo = hasOccurrenceLocationData(siniestro);
    const occurrenceLocalidad = siniestro?.lugarOcurrencia?.localidad || {};

    if (!shouldUseLugarTrabajo && savedDraft) {
        return {
            calle: savedDraft.calle,
            altura: savedDraft.altura,
            piso: savedDraft.piso,
            depto: savedDraft.depto,
            codigoPostal: savedDraft.codigoPostal
        };
    }

    const direccion = shouldUseLugarTrabajo
        ? siniestro?.lugarOcurrencia || {}
        : siniestro?.establecimiento?.direccion || {};
    const localidad = shouldUseLugarTrabajo
        ? {
              codigoPostal:
                  siniestro?.lugarOcurrencia?.codigoPostal || occurrenceLocalidad?.codigoPostal
          }
        : direccion?.localidad || {};

    return {
        calle: direccion?.calle || '',
        altura: direccion?.altura || '',
        piso: direccion?.piso || '',
        depto: direccion?.depto || direccion?.departamento || '',
        codigoPostal: localidad?.codigoPostal ? String(localidad.codigoPostal) : ''
    };
};

const getInitialOccurrenceProvince = (
    siniestro?: any,
    savedDraft?: OccurrenceDraft | null
): ProvinceOption | null => {
    if (!hasOccurrenceLocationData(siniestro) && savedDraft?.selectedProvince) {
        return savedDraft.selectedProvince;
    }

    const occurrenceProvincia = siniestro?.lugarOcurrencia?.localidad?.provincia;
    const provincia =
        occurrenceProvincia || siniestro?.establecimiento?.direccion?.localidad?.provincia;

    if (!provincia?.id) return null;

    return {
        id: Number(provincia.id),
        nombre: provincia.nombre || ''
    };
};

const getInitialOccurrenceCity = (
    siniestro?: any,
    savedDraft?: OccurrenceDraft | null
): CityOption | null => {
    const shouldUseLugarTrabajo = hasOccurrenceLocationData(siniestro);

    if (!shouldUseLugarTrabajo && savedDraft?.selectedCity) {
        return savedDraft.selectedCity;
    }

    const lugarOcurrencia = siniestro?.lugarOcurrencia || {};
    const establecimientoLocalidad = siniestro?.establecimiento?.direccion?.localidad;
    const localidad = shouldUseLugarTrabajo
        ? {
              id: lugarOcurrencia?.localidad?.id || lugarOcurrencia?.idLocalidad,
              nombre:
                  lugarOcurrencia?.localidad?.nombre ||
                  (establecimientoLocalidad?.id === lugarOcurrencia?.idLocalidad
                      ? establecimientoLocalidad?.nombre
                      : ''),
              codigoPostal:
                  lugarOcurrencia?.codigoPostal || lugarOcurrencia?.localidad?.codigoPostal
          }
        : establecimientoLocalidad;

    if (!localidad?.id) return null;

    return {
        id: Number(localidad.id),
        nombre: localidad.nombre || '',
        codigoPostal: localidad.codigoPostal
    };
};

function SiniestroInformationTab({
    siniestro,
    siniestroId,
    canEditOccurrenceLocation,
    isLoading,
    onRefreshSiniestro
}: SiniestroInformationTabProps) {
    const { t } = useTranslation('siniestrosRoam');
    const accidentado = siniestro?.accidentado || {};
    const empleador = siniestro?.empleador || {};
    const establecimiento = siniestro?.establecimiento || {};
    const organismo = establecimiento?.organismo || {};
    // "Contrato" y "Organismo" (Escenario-07) salen de datosSimp, no de establecimiento.organismo
    // (ese último queda siempre vacío porque `siniestro.establecimiento` no existe en la API real).
    const contratoOrganismo = siniestro?.datosSimp?.organismo || {};
    const contratoOrganismoCentral = contratoOrganismo?.organismoCentral || {};
    const lugarTrabajo = siniestro?.lugarTrabajo || {};
    const datosMedicos = siniestro?.datosMedicos || {};
    const empty = t('detail.placeholders.empty');
    const { data: provincesList, isLoading: isLoadingProvinces } = useGetProvincesQuery(null);
    const [getCities, { data: citiesList, isLoading: isLoadingCities }] = useLazyGetCitiesQuery();
    const [guardarLugarOcurrencia, { isLoading: isSavingOccurrenceLocation }] =
        useGuardarLugarOcurrenciaMutation();
    const [isRefreshingOccurrenceLocation, setIsRefreshingOccurrenceLocation] = useState(false);
    const [savedOccurrenceDraft, setSavedOccurrenceDraft] = useState<OccurrenceDraft | null>(null);
    const [occurrenceFormValues, setOccurrenceFormValues] = useState(
        getOccurrenceFormInitialValues(siniestro)
    );
    const [occurrenceFormErrors, setOccurrenceFormErrors] = useState<OccurrenceFormErrors>({});
    const [hasSavedOccurrenceLocation, setHasSavedOccurrenceLocation] = useState(
        hasOccurrenceLocationData(siniestro)
    );
    const [isOccurrenceEditMode, setIsOccurrenceEditMode] = useState(
        !hasOccurrenceLocationData(siniestro) && canEditOccurrenceLocation
    );
    const [selectedProvince, setSelectedProvince] = useState<ProvinceOption | null>(
        getInitialOccurrenceProvince(siniestro)
    );
    const [selectedCity, setSelectedCity] = useState<CityOption | null>(
        getInitialOccurrenceCity(siniestro)
    );
    const [occurrenceLocalidadId, setOccurrenceLocalidadId] = useState<number | null>(
        getInitialOccurrenceCity(siniestro)?.id || null
    );

    const provinceOptions = provincesList?.data?.provincias || [];
    const cityOptions = useMemo(
        () => (selectedProvince ? citiesList?.data?.localidades || [] : []),
        [citiesList?.data?.localidades, selectedProvince]
    );

    useEffect(() => {
        setOccurrenceFormValues(getOccurrenceFormInitialValues(siniestro, savedOccurrenceDraft));
        setOccurrenceFormErrors({});
        setHasSavedOccurrenceLocation(
            hasOccurrenceLocationData(siniestro) || !!savedOccurrenceDraft
        );
        setIsOccurrenceEditMode(
            !(hasOccurrenceLocationData(siniestro) || !!savedOccurrenceDraft) &&
                canEditOccurrenceLocation
        );
        setSelectedProvince(getInitialOccurrenceProvince(siniestro, savedOccurrenceDraft));
        setSelectedCity(getInitialOccurrenceCity(siniestro, savedOccurrenceDraft));
        setOccurrenceLocalidadId(
            getInitialOccurrenceCity(siniestro, savedOccurrenceDraft)?.id || null
        );

        if (hasOccurrenceLocationData(siniestro) && savedOccurrenceDraft) {
            setSavedOccurrenceDraft(null);
        }
    }, [siniestro, savedOccurrenceDraft, canEditOccurrenceLocation]);

    useEffect(() => {
        if (selectedProvince?.id) {
            getCities({ provinceId: selectedProvince.id });
        }
    }, [selectedProvince?.id, getCities]);

    useEffect(() => {
        if (!selectedCity?.id || !cityOptions.length) return;

        const matchedCity = cityOptions.find((city: CityOption) => city.id === selectedCity.id);
        if (!matchedCity) return;

        setSelectedCity(matchedCity);
        setOccurrenceLocalidadId(matchedCity.id);
        setOccurrenceFormValues((current) => ({
            ...current,
            codigoPostal: matchedCity.codigoPostal ? String(matchedCity.codigoPostal) : ''
        }));
    }, [cityOptions, selectedCity?.id]);

    const handleOccurrenceFieldChange =
        (field: keyof typeof occurrenceFormValues) => (event: ChangeEvent<HTMLInputElement>) => {
            setOccurrenceFormValues((current) => ({
                ...current,
                [field]: event.target.value
            }));
            setOccurrenceFormErrors((current) => ({
                ...current,
                [field]: undefined
            }));
        };

    const handleProvinceChange = (province: ProvinceOption | '' | null) => {
        const nextProvince = province && typeof province === 'object' ? province : null;

        setSelectedProvince(nextProvince);
        setSelectedCity(null);
        setOccurrenceLocalidadId(null);
        setOccurrenceFormValues((current) => ({
            ...current,
            codigoPostal: ''
        }));
        setOccurrenceFormErrors((current) => ({
            ...current,
            idProvincia: undefined,
            idLocalidad: undefined
        }));
    };

    const handleCityChange = (city: CityOption | '' | null) => {
        const nextCity = city && typeof city === 'object' ? city : null;

        setSelectedCity(nextCity);
        setOccurrenceLocalidadId(nextCity?.id || null);
        setOccurrenceFormValues((current) => ({
            ...current,
            codigoPostal: nextCity?.codigoPostal ? String(nextCity.codigoPostal) : ''
        }));
        setOccurrenceFormErrors((current) => ({
            ...current,
            idLocalidad: undefined
        }));
    };

    const tieneEstablecimientoConDireccion =
        !!siniestro?.establecimiento?.direccion?.calle ||
        !!siniestro?.establecimiento?.direccion?.localidad?.id;

    const handleCopiarDireccionEstablecimiento = () => {
        const dir = siniestro?.establecimiento?.direccion;
        if (!dir) return;

        setOccurrenceFormValues((current) => ({
            ...current,
            ...(dir.calle ? { calle: dir.calle } : {}),
            ...(dir.altura ? { altura: dir.altura } : {}),
            ...(dir.piso != null ? { piso: dir.piso } : {}),
            ...(dir.depto != null ? { depto: dir.depto } : {})
        }));

        const provinciaId = dir.localidad?.provincia?.id;
        const localidadId = dir.localidad?.id;
        const localidadNombre = dir.localidad?.nombre;
        const codigoPostal = dir.localidad?.codigoPostal;

        if (provinciaId) {
            const matchedProvince = provinceOptions.find(
                (p: ProvinceOption) => p.id === provinciaId
            );
            if (matchedProvince) {
                setSelectedProvince(matchedProvince);
                getCities({ provinceId: matchedProvince.id });
            }
        }

        if (localidadId) {
            const tentativeCity: CityOption = {
                id: localidadId,
                nombre: localidadNombre || '',
                ...(codigoPostal ? { codigoPostal } : {})
            };
            setSelectedCity(tentativeCity);
            setOccurrenceLocalidadId(localidadId);
            if (codigoPostal) {
                setOccurrenceFormValues((current) => ({
                    ...current,
                    codigoPostal: String(codigoPostal)
                }));
            }
        }
    };

    const handleCancelOccurrenceEdit = () => {
        setOccurrenceFormValues(getOccurrenceFormInitialValues(siniestro, savedOccurrenceDraft));
        setOccurrenceFormErrors({});
        setSelectedProvince(getInitialOccurrenceProvince(siniestro, savedOccurrenceDraft));
        setSelectedCity(getInitialOccurrenceCity(siniestro, savedOccurrenceDraft));
        setOccurrenceLocalidadId(
            getInitialOccurrenceCity(siniestro, savedOccurrenceDraft)?.id || null
        );
        setIsOccurrenceEditMode(!hasSavedOccurrenceLocation && canEditOccurrenceLocation);
    };

    const validateOccurrenceForm = () => {
        const nextErrors: OccurrenceFormErrors = {};

        if (!occurrenceFormValues.calle.trim()) {
            nextErrors.calle = t('detail.occurrenceLocation.validations.streetRequired') as string;
        }

        if (!occurrenceFormValues.altura.trim()) {
            nextErrors.altura = t(
                'detail.occurrenceLocation.validations.streetNumberRequired'
            ) as string;
        }

        if (!selectedProvince?.id) {
            nextErrors.idProvincia = t(
                'detail.occurrenceLocation.validations.provinceRequired'
            ) as string;
        }

        if (!occurrenceLocalidadId) {
            nextErrors.idLocalidad = t(
                'detail.occurrenceLocation.validations.localityRequired'
            ) as string;
        }

        setOccurrenceFormErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const isOccurrenceLocationLoading =
        isSavingOccurrenceLocation || isRefreshingOccurrenceLocation;

    const handleSaveOccurrenceLocation = async () => {
        if (!siniestroId || !occurrenceLocalidadId || isOccurrenceLocationLoading) return;
        if (!validateOccurrenceForm()) return;

        try {
            setSavedOccurrenceDraft({
                calle: occurrenceFormValues.calle,
                altura: occurrenceFormValues.altura,
                piso: occurrenceFormValues.piso,
                depto: occurrenceFormValues.depto,
                codigoPostal: occurrenceFormValues.codigoPostal,
                selectedProvince,
                selectedCity
            });

            await guardarLugarOcurrencia({
                id: siniestroId,
                body: {
                    calle: occurrenceFormValues.calle,
                    altura: occurrenceFormValues.altura,
                    piso: occurrenceFormValues.piso,
                    depto: occurrenceFormValues.depto,
                    codigoPostal: occurrenceFormValues.codigoPostal,
                    idLocalidad: occurrenceLocalidadId
                }
            }).unwrap();

            setIsRefreshingOccurrenceLocation(true);
            await onRefreshSiniestro();
            setHasSavedOccurrenceLocation(true);
            setIsOccurrenceEditMode(false);
            enqueueSnackbar(t('detail.occurrenceLocation.alerts.saveSuccess'), {
                variant: 'success'
            });
        } catch (error: any) {
            setSavedOccurrenceDraft(null);
            enqueueSnackbar(
                error?.data?.message || t('detail.occurrenceLocation.alerts.saveError'),
                {
                    variant: 'error'
                }
            );
        } finally {
            setIsRefreshingOccurrenceLocation(false);
        }
    };

    const hasValidOccurrenceLocation =
        Boolean(occurrenceFormValues.calle?.trim()) &&
        Boolean(occurrenceFormValues.altura?.trim()) &&
        Boolean(selectedProvince?.id) &&
        Boolean(occurrenceLocalidadId);
    const shouldShowOccurrenceRequiredChip = !isLoading && !hasValidOccurrenceLocation;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
            <Section
                title={t('detail.occurrenceLocation.cardTitle')}
                titleAdornment={
                    shouldShowOccurrenceRequiredChip ? (
                        <Chip
                            icon={
                                <EditIcon
                                    sx={{
                                        color: '#B42318 !important',
                                        fontSize: '12px !important'
                                    }}
                                />
                            }
                            label="Requerido"
                            size="small"
                            sx={{
                                height: 20,
                                borderRadius: 1,
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#B42318',
                                backgroundColor: '#FEECEC',
                                border: '1px solid #F5CACA',
                                '& .MuiChip-label': {
                                    px: 0.75
                                },
                                '& .MuiChip-icon': {
                                    ml: 0.75,
                                    mr: -0.25
                                }
                            }}
                        />
                    ) : null
                }
            >
                {isOccurrenceLocationLoading ? (
                    <Box
                        sx={{
                            minHeight: 220,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <CircularProgress size={32} thickness={5} />
                    </Box>
                ) : isOccurrenceEditMode ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        {tieneEstablecimientoConDireccion && (
                            <Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleCopiarDireccionEstablecimiento}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    Tomar datos del lugar de trabajo del empleado
                                </Button>
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                gap: 2
                            }}
                        >
                            <TextField
                                label={t('detail.occurrenceLocation.fields.street')}
                                value={occurrenceFormValues.calle}
                                onChange={handleOccurrenceFieldChange('calle')}
                                fullWidth
                                size="small"
                                error={!!occurrenceFormErrors.calle}
                                helperText={occurrenceFormErrors.calle}
                                sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}
                            />
                            <TextField
                                label={t('detail.occurrenceLocation.fields.streetNumber')}
                                value={occurrenceFormValues.altura}
                                onChange={handleOccurrenceFieldChange('altura')}
                                fullWidth
                                size="small"
                                error={!!occurrenceFormErrors.altura}
                                helperText={occurrenceFormErrors.altura}
                            />
                            <TextField
                                label={t('detail.occurrenceLocation.fields.floor')}
                                value={occurrenceFormValues.piso}
                                onChange={handleOccurrenceFieldChange('piso')}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label={t('detail.occurrenceLocation.fields.apartment')}
                                value={occurrenceFormValues.depto}
                                onChange={handleOccurrenceFieldChange('depto')}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label={t('detail.occurrenceLocation.fields.postalCode')}
                                value={occurrenceFormValues.codigoPostal}
                                onChange={handleOccurrenceFieldChange('codigoPostal')}
                                fullWidth
                                size="small"
                                disabled
                            />
                            <Box>
                                <AutocompleteComponent
                                    loading={isLoadingProvinces}
                                    value={selectedProvince}
                                    onChange={handleProvinceChange}
                                    name="idProvincia"
                                    label={t('detail.occurrenceLocation.fields.province')}
                                    options={provinceOptions}
                                    fullWidth
                                    size="small"
                                    showClearIcon
                                    errors={occurrenceFormErrors}
                                />
                            </Box>
                            <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
                                <AutocompleteComponent
                                    loading={isLoadingCities}
                                    value={selectedCity}
                                    onChange={handleCityChange}
                                    name="idLocalidad"
                                    label={t('detail.occurrenceLocation.fields.locality')}
                                    options={cityOptions}
                                    fullWidth
                                    size="small"
                                    disabled={!selectedProvince}
                                    errors={occurrenceFormErrors}
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1.5
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={handleCancelOccurrenceEdit}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                                {t('detail.occurrenceLocation.actions.cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSaveOccurrenceLocation}
                                disabled={
                                    !siniestroId ||
                                    !occurrenceLocalidadId ||
                                    isOccurrenceLocationLoading
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    boxShadow: 'none'
                                }}
                            >
                                {isOccurrenceLocationLoading ? (
                                    <CircularProgress size={18} color="inherit" thickness={5} />
                                ) : (
                                    t('detail.occurrenceLocation.actions.save')
                                )}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                gap: 2
                            }}
                        >
                            <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
                                <Field
                                    label={t('detail.occurrenceLocation.fields.street')}
                                    value={occurrenceFormValues.calle || empty}
                                    isLoading={isLoading}
                                />
                            </Box>
                            <Field
                                label={t('detail.occurrenceLocation.fields.streetNumber')}
                                value={occurrenceFormValues.altura || empty}
                                isLoading={isLoading}
                            />
                            <Field
                                label={t('detail.occurrenceLocation.fields.floor')}
                                value={occurrenceFormValues.piso || empty}
                                isLoading={isLoading}
                            />
                            <Field
                                label={t('detail.occurrenceLocation.fields.apartment')}
                                value={occurrenceFormValues.depto || empty}
                                isLoading={isLoading}
                            />
                            <Field
                                label={t('detail.occurrenceLocation.fields.postalCode')}
                                value={occurrenceFormValues.codigoPostal || empty}
                                isLoading={isLoading}
                            />
                            <Field
                                label={t('detail.occurrenceLocation.fields.province')}
                                value={selectedProvince?.nombre || empty}
                                isLoading={isLoading}
                            />
                            <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
                                <Field
                                    label={t('detail.occurrenceLocation.fields.locality')}
                                    value={selectedCity?.nombre || empty}
                                    isLoading={isLoading}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {canEditOccurrenceLocation && (
                                <Button
                                    variant="contained"
                                    onClick={() => setIsOccurrenceEditMode(true)}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        boxShadow: 'none'
                                    }}
                                >
                                    {t('detail.occurrenceLocation.actions.edit')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Section>

            <Section title={t('detail.sections.complaintData')}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(3, 1fr)'
                        },
                        gap: 3
                    }}
                >
                    <Field
                        label={t('detail.fields.number')}
                        value={siniestro?.nroProvisorio || empty}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.roamNumber')}
                        value={siniestro?.nroRoam || empty}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.claimNumber')}
                        value={siniestro?.nroSiniestro}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.client')}
                        value={
                            siniestro?.empleadorRazonSocial ||
                            (organismo?.cuit
                                ? `${organismo.nombre} (${organismo.cuit})`
                                : organismo?.nombre || empleador?.nombre)
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.contract')}
                        value={
                            formatNombreConCuit(
                                contratoOrganismoCentral?.nombre,
                                contratoOrganismoCentral?.cuit
                            ) || empty
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.organism')}
                        value={
                            formatNombreConCuit(contratoOrganismo?.nombre, contratoOrganismo?.cuit) ||
                            empty
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.complaintType')}
                        value={
                            siniestro?.idTipoSiniestro === 1
                                ? t('detail.types.workAccident')
                                : siniestro?.idTipoSiniestro === 3
                                  ? t('detail.types.commuteAccident')
                                  : siniestro?.idTipoSiniestro === 6
                                    ? t('detail.types.occupationalDisease')
                                    : empty
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.occurrenceDate')}
                        value={
                            siniestro?.fechaOcurrrencia
                                ? getDate(siniestro.fechaOcurrrencia)
                                : empty
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.complaintDate')}
                        value={siniestro?.fechaDenuncia ? getDate(siniestro.fechaDenuncia) : empty}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.receptionDate')}
                        value={
                            siniestro?.fechaRecepcion ? getDate(siniestro.fechaRecepcion) : empty
                        }
                        isLoading={isLoading}
                    />
                </Box>
            </Section>

            <Section title={t('detail.sections.patientData')}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(3, 1fr)'
                        },
                        gap: 3
                    }}
                >
                    <Field
                        label={t('detail.fields.document')}
                        value={accidentado?.documento}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.cuilNumber')}
                        value={accidentado?.cuil}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.patient')}
                        value={`${accidentado?.nombre || ''} ${accidentado?.apellido || ''}`.trim()}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.birthDate')}
                        value={
                            accidentado?.fechaNacimiento
                                ? getDate(accidentado.fechaNacimiento)
                                : empty
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.civilStatus')}
                        value={accidentado?.estadoCivil}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.address')}
                        value={accidentado?.datosContacto?.direccion}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.landline')}
                        value={accidentado?.datosContacto?.telefonoPrincipal}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.cellphone')}
                        value={accidentado?.datosContacto?.telefonoCelular}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.secondaryPhone')}
                        value={accidentado?.datosContacto?.telefonoSecundario}
                        isLoading={isLoading}
                    />
                </Box>
            </Section>

            <Section title={t('detail.sections.workplaceData')}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(3, 1fr)'
                        },
                        gap: 3
                    }}
                >
                    <Field
                        label={t('detail.fields.workplace')}
                        value={lugarTrabajo?.nombre}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.address')}
                        value={lugarTrabajo?.direccion}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.locality')}
                        value={lugarTrabajo?.localidad}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.occupation')}
                        value={lugarTrabajo?.ocupacion}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.province')}
                        value={lugarTrabajo?.provincia}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.entryDate')}
                        value={
                            siniestro?.datosSimp?.fechaIngreso
                                ? getDate(siniestro.datosSimp.fechaIngreso)
                                : siniestro?.datosSimp?.fechaAlta
                                  ? getDate(siniestro.datosSimp.fechaAlta)
                                  : empty
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.workSchedule')}
                        value={lugarTrabajo?.horario || t('detail.placeholders.workSchedule')}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.workPhone')}
                        value={lugarTrabajo?.telefono || t('detail.placeholders.workPhone')}
                        isLoading={isLoading}
                    />
                </Box>
            </Section>

            <Section title={t('detail.sections.medicalData')}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(3, 1fr)'
                        },
                        gap: 3
                    }}
                >
                    <Field
                        label={t('detail.fields.tracerPathology')}
                        value={getDisplayValue(
                            siniestro?.patologiaTrazadora,
                            siniestro?.patologiaAtrazadora,
                            siniestro?.patologiaAtrasadora,
                            datosMedicos?.patologiasTrazadoras,
                            datosMedicos?.patologiaTrazadora,
                            datosMedicos?.patologiaAtrazadora,
                            datosMedicos?.patologiaAtrasadora
                        )}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.medicalStatus')}
                        value={datosMedicos?.estadoMedico}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.cie10')}
                        value={datosMedicos?.cie10}
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.injurySeverity')}
                        value={
                            getDisplayValue(
                                siniestro?.severidadLesion,
                                siniestro?.severidad,
                                datosMedicos?.severidadLesion,
                                datosMedicos?.severidad
                            ) || empty
                        }
                        isLoading={isLoading}
                    />
                    <Field
                        label={t('detail.fields.certaintyDiagnosis')}
                        value={datosMedicos?.diagnosticoCerteza}
                        isLoading={isLoading}
                    />
                </Box>
            </Section>

            <Section title={t('detail.sections.story')}>
                <Field label=" " value={siniestro?.relatoHecho} isLoading={isLoading} />
            </Section>
        </Box>
    );
}

export default SiniestroInformationTab;
